<template>
  <div class="object-commission-structure">
    <v-card class="mt-4">
      <v-card-title class="text-h6"> Commission Structure </v-card-title>
      <v-card-text>
        <ValidationObserver ref="observer">
          <v-form>
            <!-- Inheritance Selection -->
            <div v-if="showInheritance" class="mb-4">
              <ValidationProvider
                v-slot="{ errors }"
                name="inheritance_type"
                rules="required"
              >
                <v-select
                  v-model="inheritanceType"
                  :items="inheritanceOptions"
                  label="Commission Structure Type*"
                  :error-messages="errors"
                  @input="emitSelectedCommissionStructure"
                />
              </ValidationProvider>
            </div>

            <!-- Commission Structure Code Field -->
            <div
              v-if="
                showInheritance &&
                (inheritanceType === 'Inherit from company' ||
                  inheritanceType === 'Custom')
              "
              class="mb-4"
            >
              <ValidationProvider
                v-slot="{ errors }"
                name="commission_structure_code"
                rules="required"
              >
                <v-text-field
                  v-model="localCommissionStructureCode"
                  label="Commission Structure Code*"
                  hint="The Omipay code or the Vector MFC lead/referral number, depending on the commission scheme."
                  persistent-hint
                  :error-messages="errors"
                  @input="emitCommissionStructureCode"
                />
              </ValidationProvider>
            </div>

            <!-- Commission Structure Content -->
            <template v-if="!showInheritance || inheritanceType === 'Custom'">
              <!-- Existing Commission Structures -->
              <div
                v-for="(commission, index) in visibleCommissionStructures"
                :key="index"
                class="commission-section mb-4"
              >
                <v-row>
                  <v-col
                    cols="12"
                    class="d-flex justify-space-between align-center"
                  >
                    <h3 class="text-subtitle-1">
                      {{
                        formatCommissionType(commission.commissionObjectType)
                      }}
                    </h3>
                    <div
                      v-if="commission.showingDeleteConfirmation"
                      class="d-flex align-center"
                    >
                      <span class="mr-2">Confirm Delete?</span>
                      <v-btn
                        icon
                        small
                        color="error"
                        @click="confirmDelete(index)"
                      >
                        <v-icon>{{ icons.mdiDelete }}</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        small
                        color="grey"
                        class="ml-2"
                        @click="cancelDelete(index)"
                      >
                        <v-icon>{{ icons.mdiClose }}</v-icon>
                      </v-btn>
                    </div>
                    <v-btn
                      v-else
                      icon
                      small
                      color="error"
                      @click="showDeleteConfirmation(index)"
                    >
                      <v-icon>{{ icons.mdiDelete }}</v-icon>
                    </v-btn>
                  </v-col>

                  <!-- Policy Commission Fields -->
                  <template
                    v-if="
                      commission.commissionObjectType === 'POLICY_COMMISSION'
                    "
                  >
                    <v-col :cols="12" :md="mobile ? 12 : 4">
                      <ValidationProvider
                        v-slot="{ errors }"
                        :name="`policy_type_${index}`"
                        rules="required"
                      >
                        <v-select
                          v-model="commission.policyCommissionAttributes.type"
                          :items="policyTypes"
                          label="Type*"
                          :error-messages="errors"
                          @input="emitCommissionData"
                        />
                      </ValidationProvider>
                    </v-col>
                    <v-col :cols="12" :md="mobile ? 12 : 4">
                      <ValidationProvider
                        v-slot="{ errors }"
                        :name="`policy_value_${index}`"
                        rules="required|decimal|min_value:0"
                      >
                        <v-text-field
                          v-model="commission.policyCommissionAttributes.value"
                          :label="
                            commission.policyCommissionAttributes.type ===
                            'PFactor'
                              ? 'Percentage*'
                              : 'Value*'
                          "
                          :suffix="
                            commission.policyCommissionAttributes.type ===
                            'PFactor'
                              ? '%'
                              : ''
                          "
                          type="number"
                          :error-messages="errors"
                          @input="emitCommissionData"
                        />
                      </ValidationProvider>
                    </v-col>
                    <v-col :cols="12" :md="mobile ? 12 : 4">
                      <ValidationProvider
                        v-slot="{ errors }"
                        :name="`policy_cadence_${index}`"
                        rules="required"
                      >
                        <v-select
                          v-model="
                            commission.policyCommissionAttributes.cadence
                          "
                          :items="policyCadences"
                          label="Cadence*"
                          :error-messages="errors"
                          @input="emitCommissionData"
                        />
                      </ValidationProvider>
                    </v-col>
                  </template>

                  <!-- Will Commission Fields -->
                  <template
                    v-if="commission.commissionObjectType === 'WILL_COMMISSION'"
                  >
                    <v-col :cols="12" :md="mobile ? 12 : 6">
                      <ValidationProvider
                        v-slot="{ errors }"
                        :name="`will_trigger_${index}`"
                        rules="required"
                      >
                        <v-select
                          v-model="commission.willCommissionAttributes.trigger"
                          :items="willTriggers"
                          label="Trigger*"
                          :error-messages="errors"
                          @input="emitCommissionData"
                        />
                      </ValidationProvider>
                    </v-col>
                    <v-col :cols="12" :md="mobile ? 12 : 6">
                      <ValidationProvider
                        v-slot="{ errors }"
                        :name="`will_value_${index}`"
                        rules="required|decimal:2|min_value:0"
                      >
                        <v-text-field
                          v-model="commission.willCommissionAttributes.value"
                          label="Value*"
                          type="number"
                          step="0.01"
                          :error-messages="errors"
                          @input="emitCommissionData"
                        />
                      </ValidationProvider>
                    </v-col>
                  </template>

                  <!-- Limit Toggle and Fields -->
                  <v-col cols="12">
                    <v-switch
                      v-model="commission.limit"
                      label="Enable Limit"
                      @change="emitCommissionData"
                    />
                  </v-col>

                  <template v-if="commission.limit">
                    <v-col :cols="12" :md="mobile ? 12 : 6">
                      <ValidationProvider
                        v-slot="{ errors }"
                        :name="`limit_type_${index}`"
                        rules="required"
                      >
                        <v-select
                          v-model="commission.selectedLimitType"
                          :items="limitTypes"
                          label="Limit Type*"
                          :error-messages="errors"
                          @input="handleLimitTypeChange(commission)"
                        />
                      </ValidationProvider>
                    </v-col>

                    <!-- Limit Amount Field -->
                    <v-col
                      v-if="commission.selectedLimitType === 'AMOUNT'"
                      :cols="12"
                      :md="mobile ? 12 : 6"
                    >
                      <ValidationProvider
                        v-slot="{ errors }"
                        :name="`limit_amount_${index}`"
                        rules="decimal|min_value:0"
                      >
                        <v-text-field
                          v-model="commission.limitAmount"
                          label="Limit Amount"
                          type="number"
                          step="0.01"
                          :error-messages="errors"
                          @input="emitCommissionData"
                        />
                      </ValidationProvider>
                    </v-col>

                    <!-- Limit Date Field -->
                    <v-col
                      v-if="commission.selectedLimitType === 'DATE'"
                      :cols="12"
                      :md="mobile ? 12 : 6"
                    >
                      <ValidationProvider
                        v-slot="{ errors }"
                        :name="`limit_date_${index}`"
                      >
                        <v-menu
                          v-model="commission.showDatePicker"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          min-width="290px"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="commission.limitDate"
                              label="Limit Date"
                              prepend-icon="mdi-calendar"
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              :error-messages="errors"
                              @input="handleDateChange(commission)"
                            />
                          </template>
                          <v-date-picker
                            v-model="commission.limitDate"
                            @input="handleDateChange(commission)"
                            no-title
                            :min="new Date().toISOString().substr(0, 10)"
                          />
                        </v-menu>
                      </ValidationProvider>
                    </v-col>
                  </template>

                  <v-col cols="12">
                    <v-switch
                      v-model="commission.active"
                      label="Active"
                      @change="emitCommissionData"
                    />
                  </v-col>
                </v-row>
              </div>

              <!-- Add New Commission Structure -->
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="selectedCommissionType"
                    :items="availableCommissionTypes"
                    label="Add Commission Type"
                    item-text="text"
                    item-value="value"
                    @change="addCommissionStructure"
                    :clearable="true"
                    return-object
                    placeholder="Add commission type"
                  />
                </v-col>
              </v-row>
            </template>
          </v-form>
        </ValidationObserver>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
  import { mdiDelete, mdiClose } from '@mdi/js';

  export default {
    name: 'ObjectCommissionStructure',
    props: {
      object_id: {
        type: String,
        required: false,
      },
      object_type: {
        type: String,
        required: true,
      },
      initialCommissionStructures: {
        type: Array,
        default: () => [],
      },
      mobile: {
        type: Boolean,
        default: false,
      },
      showInheritance: {
        type: Boolean,
        default: false,
      },
      userSelectedCommissionStructure: {
        type: String,
        required: false,
      },
      commissionStructureCode: {
        type: String,
        required: false,
        default: '',
      },
      userRole: {
        type: String,
        default: null,
      },
    },
    data() {
      return {
        icons: {
          mdiDelete,
          mdiClose,
        },
        selectedCommissionType: null,
        commissionStructures: [],
        inheritanceType:
          this.userSelectedCommissionStructure || 'No commission',
        localCommissionStructureCode: this.commissionStructureCode,
        inheritanceOptions: [
          // { text: 'No commission', value: 'No commission' },
          { text: 'Inherit from company', value: 'Inherit from company' },
          // { text: 'Custom', value: 'Custom' },
        ],
        allCommissionTypes: [
          { text: 'Policy Commission', value: 'POLICY_COMMISSION' },
          { text: 'Will Commission', value: 'WILL_COMMISSION' },
        ],
        policyTypes: [
          { text: 'P Factor', value: 'PFactor' },
          { text: 'Flat Rate', value: 'FlatRate' },
        ],
        policyCadences: [
          { text: 'Once-off', value: 'ONCE_OFF' },
          { text: 'Monthly', value: 'MONTHLY' },
        ],
        willTriggers: [
          { text: 'Wills Downloaded', value: 'WILLS_DOWNLOADED' },
          { text: 'Wills Validated', value: 'WILLS_VALIDATED' },
          { text: 'Wills Completed', value: 'WILLS_COMPLETED' },
        ],
        limitTypes: [
          { text: 'Limit Amount', value: 'AMOUNT' },
          { text: 'Limit Date', value: 'DATE' },
        ],
      };
    },
    computed: {
      visibleCommissionStructures() {
        return this.commissionStructures.filter(
          commission => !commission._destroy
        );
      },
      availableCommissionTypes() {
        const existingTypes = this.visibleCommissionStructures.map(
          commission => commission.commissionObjectType
        );
        return this.allCommissionTypes.filter(
          type => !existingTypes.includes(type.value)
        );
      },
    },
    watch: {
      initialCommissionStructures: {
        immediate: true,
        handler(newVal) {
          if (newVal && newVal.length > 0) {
            this.commissionStructures = newVal.map(commission => {
              const baseCommission = {
                id: commission.id,
                commissionObjectType: commission.commissionObjectType,
                parentObjectId: commission.parentObjectId,
                parentObjectType: commission.parentObjectType,
                active:
                  commission.active !== undefined ? commission.active : false,
                showDatePicker: false,
                limit: commission.limit || false,
                selectedLimitType: commission.limitAmount
                  ? 'AMOUNT'
                  : commission.limitDate
                  ? 'DATE'
                  : null,
                limitAmount: commission.limitAmount,
                limitDate: commission.limitDate,
              };

              if (
                commission.commissionObjectType === 'POLICY_COMMISSION' &&
                commission.policyCommission
              ) {
                baseCommission.policyCommissionAttributes = {
                  id: commission.policyCommission.id,
                  type: commission.policyCommission.type,
                  value: commission.policyCommission.value,
                  cadence: commission.policyCommission.cadence,
                };
              }

              if (
                commission.commissionObjectType === 'WILL_COMMISSION' &&
                commission.willCommission
              ) {
                baseCommission.willCommissionAttributes = {
                  id: commission.willCommission.id,
                  trigger: commission.willCommission.trigger,
                  value:
                    commission.willCommission.value != null
                      ? commission.willCommission.value.toFixed(2)
                      : null,
                };
              }

              return baseCommission;
            });
          }
        },
      },
      commissionStructures: {
        deep: true,
        handler(commissions) {
          commissions.forEach(commission => {
            if (!commission.limit) {
              commission.limitAmount = null;
              commission.limitDate = null;
              commission.selectedLimitType = null;
              this.emitCommissionData();
            }
          });
        },
      },
      userSelectedCommissionStructure: {
        immediate: true,
        handler(newVal) {
          if (newVal) {
            this.inheritanceType = newVal;
          }
        },
      },
      userRole: {
        immediate: true,
        handler(newVal) {
          if (this.showInheritance && newVal) {
            if (newVal === 'ADVISOR') {
              this.inheritanceType = 'Inherit from company';
              this.emitSelectedCommissionStructure('Inherit from company');
            } else {
              this.inheritanceType = 'No commission';
              this.emitSelectedCommissionStructure('No commission');
            }
          }
        },
      },
    },
    methods: {
      formatCommissionType(type) {
        return type
          .split('_')
          .map(
            word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(' ');
      },
      emitSelectedCommissionStructure(value) {
        this.$emit('selected-commission-structure', value);

        if (value !== 'Custom') {
          this.commissionStructures.forEach(commission => {
            if (commission.id) {
              this.$set(commission, '_destroy', true);
            }
          });
          this.commissionStructures = this.commissionStructures.filter(
            commission => commission.id
          );
          this.emitCommissionData();
        }
      },
      async validate() {
        return await this.$refs.observer.validate();
      },
      addCommissionStructure() {
        if (!this.selectedCommissionType) return;

        const newCommission = {
          commissionObjectType: this.selectedCommissionType.value,
          parentObjectId: this.object_id,
          parentObjectType: this.object_type,
          active: false,
          showDatePicker: false,
          limit: false,
          selectedLimitType: null,
          limitAmount: null,
          limitDate: null,
          policyCommissionAttributes:
            this.selectedCommissionType.value === 'POLICY_COMMISSION'
              ? {
                  type: null,
                  value: null,
                  cadence: null,
                }
              : null,
          willCommissionAttributes:
            this.selectedCommissionType.value === 'WILL_COMMISSION'
              ? {
                  trigger: null,
                  value: null,
                }
              : null,
        };

        this.commissionStructures.push(newCommission);
        this.$nextTick(() => {
          this.selectedCommissionType = null;
        });
        this.emitCommissionData();
      },
      removeCommission(index) {
        const commission = this.visibleCommissionStructures[index];
        if (commission.id) {
          this.$set(commission, '_destroy', true);
        } else {
          const actualIndex = this.commissionStructures.indexOf(commission);
          this.commissionStructures.splice(actualIndex, 1);
        }
        this.commissionStructures = [...this.commissionStructures];
        this.emitCommissionData();
      },
      emitCommissionData() {
        this.$emit('commission-data-updated', {
          objectCommissionPairs: this.commissionStructures.map(commission => {
            const baseData = {
              id: commission.id,
              _destroy: commission._destroy,
              commissionObjectType: commission.commissionObjectType,
              parentObjectId: commission.parentObjectId,
              parentObjectType: commission.parentObjectType,
              active: commission.active,
              limit: commission.limit,
              limitAmount: commission.limitAmount
                ? parseFloat(commission.limitAmount)
                : null,
              limitDate: commission.limitDate,
            };

            if (
              commission.commissionObjectType === 'POLICY_COMMISSION' &&
              commission.policyCommissionAttributes
            ) {
              baseData.policyCommissionAttributes = {
                id: commission.policyCommissionAttributes.id,
                type: commission.policyCommissionAttributes.type,
                value: commission.policyCommissionAttributes.value
                  ? parseFloat(commission.policyCommissionAttributes.value)
                  : null,
                cadence: commission.policyCommissionAttributes.cadence,
              };
            }

            if (
              commission.commissionObjectType === 'WILL_COMMISSION' &&
              commission.willCommissionAttributes
            ) {
              baseData.willCommissionAttributes = {
                id: commission.willCommissionAttributes.id,
                trigger: commission.willCommissionAttributes.trigger,
                value: commission.willCommissionAttributes.value
                  ? parseFloat(commission.willCommissionAttributes.value)
                  : null,
              };
            }

            return baseData;
          }),
          commissionStructure: this.inheritanceType,
        });
      },
      showDeleteConfirmation(index) {
        const commission = this.visibleCommissionStructures[index];
        if (commission) {
          this.$set(commission, 'showingDeleteConfirmation', true);
        }
      },
      cancelDelete(index) {
        const commission = this.visibleCommissionStructures[index];
        if (commission) {
          this.$set(commission, 'showingDeleteConfirmation', false);
        }
      },
      confirmDelete(index) {
        const commission = this.visibleCommissionStructures[index];
        if (commission) {
          if (commission.id) {
            this.$set(commission, '_destroy', true);
          } else {
            const actualIndex = this.commissionStructures.indexOf(commission);
            this.commissionStructures.splice(actualIndex, 1);
          }
          this.commissionStructures = [...this.commissionStructures];
          this.emitCommissionData();
        }
      },
      handleLimitTypeChange(commission) {
        if (commission.selectedLimitType === 'AMOUNT') {
          commission.limitDate = null;
        } else if (commission.selectedLimitType === 'DATE') {
          commission.limitAmount = null;
        }
        this.emitCommissionData();
      },
      handleDateChange(commission) {
        commission.showDatePicker = false;
        this.emitCommissionData();
      },
      emitCommissionStructureCode() {
        this.$emit(
          'commission-structure-code',
          this.localCommissionStructureCode
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  .object-commission-structure {
    margin-bottom: 24px;
  }

  .commission-section {
    padding: 16px;
    background-color: #f5f5f5;
    border-radius: 4px;
    margin-top: 8px;
    position: relative;
  }
</style>
